import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Users, Building2, Upload } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import axios from 'axios';

export default function MembershipRegister() {
  const navigate = useNavigate();
  const location = useLocation();
  const selectedPlan = location.state?.selectedPlan;
  
  const [memberType, setMemberType] = useState('individual');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Common fields
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    
    // Individual fields
    profession: '',
    area_of_expertise: '',
    organization: '',
    
    // Institutional fields
    organization_name: '',
    organization_type: '',
    registration_number: '',
    sector_focus: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    // Common validations
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!/^\+\d{10,15}$/.test(formData.phone.replace(/[\s-]/g, ''))) {
      newErrors.phone = 'Phone must be in international format (+91XXXXXXXXXX)';
    }
    if (!formData.password) newErrors.password = 'Password is required';
    if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    // Type-specific validations
    if (memberType === 'individual') {
      if (!formData.profession.trim()) newErrors.profession = 'Profession is required';
    } else {
      if (!formData.organization_name.trim()) {
        newErrors.organization_name = 'Organization name is required';
      }
      if (!formData.organization_type) {
        newErrors.organization_type = 'Organization type is required';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    if (!selectedPlan) {
      alert('Please select a membership plan first');
      navigate('/membership');
      return;
    }
    
    setLoading(true);
    
    try {
      // Register member
      const registrationData = {
        email: formData.email.toLowerCase(),
        name: formData.name,
        phone: formData.phone.replace(/[\s-]/g, ''),
        password: formData.password,
        member_type: memberType,
        plan_id: selectedPlan.id,
        
        // Conditional fields
        ...(memberType === 'individual' ? {
          profession: formData.profession,
          area_of_expertise: formData.area_of_expertise,
          organization: formData.organization
        } : {
          organization_name: formData.organization_name,
          organization_type: formData.organization_type,
          registration_number: formData.registration_number,
          sector_focus: formData.sector_focus
        })
      };
      
      const { data } = await axios.post(
        `${API_BASE_URL}/api/membership/register`,
        registrationData
      );
      
      // If free plan or institutional (needs verification), redirect to dashboard
      if (selectedPlan.price === 0 || memberType === 'institutional') {
        alert(memberType === 'institutional' 
          ? 'Registration successful! Your account is pending verification. You will receive an email once approved.'
          : 'Registration successful! You can now login to access the platform.'
        );
        navigate('/login');
      } else {
        // For paid individual plans, proceed to payment
        navigate('/membership/payment', { 
          state: { 
            member: data,
            plan: selectedPlan
          } 
        });
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      alert(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8 max-w-3xl">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/membership')}
            className="mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Plans
          </Button>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2">
              Create Your Account
            </h1>
            {selectedPlan && (
              <p className="text-gray-600">
                You're registering for: <span className="font-semibold text-[#E87722]">
                  {selectedPlan.plan_name}
                </span>
                {selectedPlan.price > 0 && (
                  <> - ₹{selectedPlan.price.toLocaleString('en-IN')}/year</>
                )}
              </p>
            )}
          </div>

          {/* Membership Type Toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-white rounded-lg p-2 shadow-md inline-flex">
              <button
                type="button"
                onClick={() => setMemberType('individual')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  memberType === 'individual'
                    ? 'bg-[#E87722] text-white'
                    : 'text-gray-600 hover:text-[#E87722]'
                }`}
              >
                <Users className="h-4 w-4 inline mr-2" />
                Individual
              </button>
              <button
                type="button"
                onClick={() => setMemberType('institutional')}
                className={`px-6 py-2 rounded-md font-medium transition-all ${
                  memberType === 'institutional'
                    ? 'bg-[#E87722] text-white'
                    : 'text-gray-600 hover:text-[#E87722]'
                }`}
              >
                <Building2 className="h-4 w-4 inline mr-2" />
                Institutional
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <Card className="p-8">
              {/* Common Fields */}
              <div className="space-y-6 mb-8">
                <h3 className="text-lg font-bold text-[#1E3A5F]">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {memberType === 'individual' ? 'Full Name' : 'Contact Person Name'} *
                  </label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email Address *</label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number *</label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                  />
                  <p className="text-xs text-gray-500 mt-1">International format with country code</p>
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Password *</label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Minimum 8 characters"
                    />
                    {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Confirm Password *</label>
                    <Input
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Re-enter password"
                    />
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Individual-specific Fields */}
              {memberType === 'individual' && (
                <div className="space-y-6 border-t pt-8">
                  <h3 className="text-lg font-bold text-[#1E3A5F]">Professional Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Profession *</label>
                    <Input
                      type="text"
                      value={formData.profession}
                      onChange={(e) => setFormData({ ...formData, profession: e.target.value })}
                      placeholder="e.g., Development Consultant, Researcher"
                    />
                    {errors.profession && (
                      <p className="text-red-500 text-sm mt-1">{errors.profession}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Area of Expertise</label>
                    <Input
                      type="text"
                      value={formData.area_of_expertise}
                      onChange={(e) => setFormData({ ...formData, area_of_expertise: e.target.value })}
                      placeholder="e.g., Social Development, Policy Analysis"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Current Organization (Optional)
                    </label>
                    <Input
                      type="text"
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      placeholder="Your employer or affiliated organization"
                    />
                  </div>
                </div>
              )}

              {/* Institutional-specific Fields */}
              {memberType === 'institutional' && (
                <div className="space-y-6 border-t pt-8">
                  <h3 className="text-lg font-bold text-[#1E3A5F]">Organization Details</h3>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Organization Name *</label>
                    <Input
                      type="text"
                      value={formData.organization_name}
                      onChange={(e) => setFormData({ ...formData, organization_name: e.target.value })}
                      placeholder="Full legal name of organization"
                    />
                    {errors.organization_name && (
                      <p className="text-red-500 text-sm mt-1">{errors.organization_name}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Organization Type *</label>
                    <select
                      value={formData.organization_type}
                      onChange={(e) => setFormData({ ...formData, organization_type: e.target.value })}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="">Select Type</option>
                      <option value="NGO">NGO / Non-Profit</option>
                      <option value="Corporate">Corporate / CSR</option>
                      <option value="MSME">MSME / Small Business</option>
                      <option value="Academic">Academic / Research Institution</option>
                      <option value="Government">Government Body</option>
                    </select>
                    {errors.organization_type && (
                      <p className="text-red-500 text-sm mt-1">{errors.organization_type}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Registration Number</label>
                    <Input
                      type="text"
                      value={formData.registration_number}
                      onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                      placeholder="Company/Trust registration number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Sector Focus</label>
                    <Input
                      type="text"
                      value={formData.sector_focus}
                      onChange={(e) => setFormData({ ...formData, sector_focus: e.target.value })}
                      placeholder="e.g., Education, Healthcare, Environment"
                    />
                  </div>

                  {/* Verification Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                      <strong>📝 Verification Required:</strong> Institutional memberships require 
                      document verification. After registration, you'll be able to upload required 
                      documents (registration certificate, PAN, etc.). Your account will be activated 
                      once verified by our team.
                    </p>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-8 pt-6 border-t">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#E87722] hover:bg-[#d66a1a] py-6 text-lg"
                >
                  {loading ? 'Creating Account...' : (
                    selectedPlan?.price > 0 && memberType === 'individual'
                      ? 'Continue to Payment'
                      : 'Create Account'
                  )}
                </Button>
                
                <p className="text-xs text-center text-gray-500 mt-4">
                  By registering, you agree to CSED's Terms of Service and Privacy Policy
                </p>
              </div>
            </Card>
          </form>

          {/* Already have account */}
          <p className="text-center mt-6 text-gray-600">
            Already have an account?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-[#E87722] font-medium hover:underline"
            >
              Login here
            </button>
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
}
